// Lib
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';

// Utils
import { InAppReviewPlugin } from '../../capacitor_plugins/inAppReview';
import { checkAndUpdateSessionCount, shouldPromptForReview, markPromptedForReview } from './mobileAppReviewService';
import { isDevelopmentMode, isPlatformNativeMobileApp } from '../platform/utils/platformDetailsUtils';
import usePrevious from '../utils/react/usePrevious';

// Selectors
import { getPlatformDetailsSelector } from '../platform/platformSelector';
import { contentTotalCountSelector } from '../user/elementCount/elementCountSelector';
import { selectCurrentToolbarType } from '../workspace/toolbar/selector/currentToolbarSelector';
import { isUserSubscribed } from '../user/currentUserSelector';

// Types
import { ToolbarListType } from '../workspace/toolbar/toolbarTypes';
import { CAPACITOR_IN_APP_REVIEW_PLUGIN_NAME } from '../../capacitor_plugins/pluginConstants';

const useShowMobileAppReviewPrompt = () => {
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const userContentCount = useSelector(contentTotalCountSelector);
    const currentToolbarType = useSelector(selectCurrentToolbarType);
    const isMobileAppMode = isPlatformNativeMobileApp(platformDetails);
    const isSubscribedUser = useSelector(isUserSubscribed);

    // Track previous toolbar state
    const previousToolbarType = usePrevious(currentToolbarType);

    // Dont prompt for review in development mode as it can get annoying
    const isDevMode = isDevelopmentMode();

    // Track user sessions when the hook is used
    useEffect(() => {
        if (!isMobileAppMode || isDevMode) return;

        // Check if this is a new session and update the session count
        checkAndUpdateSessionCount();
    }, [isMobileAppMode]);

    // Monitor for toolbar resets to default state
    useEffect(() => {
        if (!isMobileAppMode || isDevMode) return;

        if (!Capacitor.isPluginAvailable(CAPACITOR_IN_APP_REVIEW_PLUGIN_NAME)) return;

        const isDefaultToolbar = currentToolbarType === ToolbarListType.MOBILE_TABS;

        const wasNonDefaultToolbar =
            previousToolbarType !== undefined && previousToolbarType !== ToolbarListType.MOBILE_TABS;

        // Only trigger when toolbar has just reset from non-default to default state
        // Ensure previousToolbarType exists (not the initial undefined state)
        const hasJustResetToDefault = isDefaultToolbar && wasNonDefaultToolbar;

        if (hasJustResetToDefault && shouldPromptForReview(userContentCount, isSubscribedUser)) {
            // Mark as prompted before requesting the review to prevent multiple prompts
            markPromptedForReview();
            InAppReviewPlugin.requestReview().catch((error) => {
                console.error('Failed to request app review:', error);
            });
        }
    }, [isMobileAppMode, currentToolbarType, previousToolbarType, userContentCount, isSubscribedUser]);
};

export default useShowMobileAppReviewPrompt;
